import React, { useState, useEffect } from 'react';
import AdvancedCalendar from './AdvancedCalendar';
import MobileMonthCalendar from './MobileMonthCalendar';
import { ChevronDown, ArrowLeft, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface AssistantProfessional {
  professional_id: number;
  nombre: string;
  apellido: string;
  especialidad: string;
  professional_type: string;
  picture?: string;
  email?: string;
}

const ResponsiveCalendar: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isAssistant, setIsAssistant] = useState(false);
  const [professionals, setProfessionals] = useState<AssistantProfessional[]>([]);
  const [selectedProfessional, setSelectedProfessional] = useState<AssistantProfessional | null>(null);
  const [loadingProfs, setLoadingProfs] = useState(false);
  const navigate = useNavigate();

  // Detectar tamaño de pantalla
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Detectar si es asistente y cargar profesionales
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      if (user.is_assistant || user.role === 'assistant') {
        setIsAssistant(true);
        loadProfessionals();
      }
    }
  }, []);

  const loadProfessionals = async () => {
    setLoadingProfs(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/assistants/my-professionals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.professionals?.length > 0) {
        setProfessionals(data.professionals);
        setSelectedProfessional(data.professionals[0]);
      }
    } catch (err) {
      console.error('Error cargando profesionales:', err);
    } finally {
      setLoadingProfs(false);
    }
  };

  // Construir overrideUser a partir del profesional seleccionado
  const buildOverrideUser = () => {
    if (!selectedProfessional) return null;
    return {
      id: selectedProfessional.professional_id,
      email: selectedProfessional.email || '',
      nombre: selectedProfessional.nombre,
      apellido: selectedProfessional.apellido,
      es_profesional: true,
      professional_type: (selectedProfessional.professional_type || 'salud') as 'salud' | 'belleza',
      especialidad: selectedProfessional.especialidad,
      picture: selectedProfessional.picture
    };
  };

  // Loading
  if (isAssistant && loadingProfs) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  // Sin profesionales
  if (isAssistant && professionals.length === 0 && !loadingProfs) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No tenés profesionales asignados</p>
          <button onClick={() => navigate('/assistant/dashboard')} className="mt-4 text-blue-600 hover:underline text-sm">
            Volver al dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Barra de asistente con selector de profesional */}
      {isAssistant && professionals.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <button
              onClick={() => navigate('/assistant/dashboard')}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Dashboard
            </button>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 hidden sm:inline">Calendario de:</span>
              <div className="relative">
                <select
                  value={selectedProfessional?.professional_id || ''}
                  onChange={(e) => {
                    const prof = professionals.find(p => p.professional_id === Number(e.target.value));
                    if (prof) setSelectedProfessional(prof);
                  }}
                  className="appearance-none bg-blue-50 border border-blue-200 text-blue-800 rounded-lg pl-3 pr-8 py-2 text-sm font-medium focus:ring-2 focus:ring-blue-300 focus:border-blue-300 cursor-pointer"
                >
                  {professionals.map(prof => (
                    <option key={prof.professional_id} value={prof.professional_id}>
                      {prof.nombre} {prof.apellido} — {prof.especialidad}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calendario */}
      {isMobile ? (
        <MobileMonthCalendar
          key={`mobile-${selectedProfessional?.professional_id || 'default'}`}
          overrideUser={isAssistant ? buildOverrideUser() : undefined}
        />
      ) : (
        <AdvancedCalendar
          key={selectedProfessional?.professional_id || 'default'}
          overrideUser={isAssistant ? buildOverrideUser() : undefined}
        />
      )}
    </div>
  );
};

export default ResponsiveCalendar;